import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Building2, Upload, Save, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

import { assetUrl } from '../lib/assetUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FIELDS = [
  { key: 'company_name', label: 'Firmanavn', type: 'text' },
  { key: 'org_number', label: 'Org.nr', type: 'text' },
  { key: 'phone', label: 'Telefon', type: 'text' },
  { key: 'email', label: 'E-post', type: 'email' },
  { key: 'address', label: 'Adresse', type: 'text', full: true },
  { key: 'postal_code', label: 'Postnummer', type: 'text' },
  { key: 'city', label: 'Sted', type: 'text' },
];

export const CompanySettingsCard = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/company-settings`);
      setSettings(res.data);
    } catch {
      toast.error('Kunne ikke hente selskapsinnstillinger');
    }
  };

  const handleChange = (key, value) => setSettings((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/company-settings`, settings);
      setSettings(res.data);
      window.dispatchEvent(new Event('company-settings-updated'));
      toast.success('Selskapsinnstillinger lagret');
    } catch {
      toast.error('Kunne ikke lagre innstillinger');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Kun bildefiler er tillatt');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/company-settings/logo`, formData);
      setSettings((prev) => ({ ...prev, logo_url: res.data.logo_url }));
      window.dispatchEvent(new Event('company-settings-updated'));
      toast.success('Logo oppdatert');
    } catch {
      toast.error('Kunne ikke laste opp logo');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  if (!settings) return null;

  const logoSrc = assetUrl(settings.logo_url);

  return (
    <Card className="mb-8" data-testid="company-settings-card">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Building2 className="h-5 w-5 text-brand-600" />
          <CardTitle>Selskapsinformasjon</CardTitle>
        </div>
        <CardDescription>Oppdater logo, adresse, org.nr og kontaktinfo. Vises bl.a. på PDF-dokumenter.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Logo */}
        <div className="flex items-center gap-4">
          <div className="h-20 w-20 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
            {logoSrc ? (
              <img src={logoSrc} alt="Logo" className="h-full w-full object-contain" data-testid="company-logo-preview" />
            ) : (
              <ImageIcon className="h-8 w-8 text-slate-300" />
            )}
          </div>
          <div>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-testid="company-logo-input" />
            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="company-logo-upload">
              <Upload className="h-4 w-4 mr-2" />
              {uploading ? 'Laster opp...' : 'Last opp logo'}
            </Button>
            <p className="text-xs text-slate-500 mt-1.5">PNG eller JPG. Vises på monteringsskjema-PDF.</p>
          </div>
        </div>

        {/* Fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {FIELDS.map((f) => (
            <div key={f.key} className={`space-y-2 ${f.full ? 'sm:col-span-2' : ''}`}>
              <Label htmlFor={`company-${f.key}`}>{f.label}</Label>
              <Input
                id={`company-${f.key}`}
                data-testid={`company-field-${f.key}`}
                type={f.type}
                value={settings[f.key] || ''}
                onChange={(e) => handleChange(f.key, e.target.value)}
              />
            </div>
          ))}
          <div className="space-y-2">
            <Label htmlFor="company-installation_title">Tittel på monteringsskjema (PDF)</Label>
            <Input
              id="company-installation_title"
              data-testid="company-field-installation_title"
              value={settings.installation_title || ''}
              onChange={(e) => handleChange('installation_title', e.target.value)}
              placeholder="MONTERINGSSKJEMA"
              maxLength={60}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="company-installation_subtitle">Undertekst (valgfri)</Label>
            <Input
              id="company-installation_subtitle"
              data-testid="company-field-installation_subtitle"
              value={settings.installation_subtitle || ''}
              onChange={(e) => handleChange('installation_subtitle', e.target.value)}
              placeholder="F.eks. Installasjon og overlevering av anlegg"
              maxLength={100}
            />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="company-document_footer">Bunntekst på dokumenter</Label>
            <Textarea
              id="company-document_footer"
              data-testid="company-field-document_footer"
              rows={2}
              value={settings.document_footer || ''}
              onChange={(e) => handleChange('document_footer', e.target.value)}
              placeholder="F.eks. kontaktinfo eller vilkår som vises nederst på PDF"
            />
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="company-settings-save">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Lagrer...' : 'Lagre endringer'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default CompanySettingsCard;
